import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    FileText,
    FolderOpen,
    LayoutDashboard,
    Menu,
    Users,
    Activity,
    Palette
} from 'lucide-react';
import edgeLogo from '../assets/images/edge_logo-removebg.png';
import UserAvatar from '../shared/components/ui/UserAvatar';
import './Sidebar.css';

const Sidebar = ({ user, isCollapsed, setIsCollapsed }) => {
    const getInitials = (name) => {
        if (!name) return '??';
        return name
            .split(' ')
            .filter(Boolean)
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    const displayName = user
        ? (user.employee_name || user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim())
        : 'Jacob Shahjan';
    const displayRole = user?.designation || (user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Bid Manager');
    const displayInitials = getInitials(displayName);

    const userDesignation = (user?.designation || '').toLowerCase();
    const userRole = (user?.role || '').toLowerCase();

    // isManager: mirrors the authoritative check in RFPAnalysisResult.jsx (designation + role)
    const isManager = (
        userDesignation.includes('lead') || userDesignation.includes('manager') || userDesignation.includes('admin') ||
        userRole.includes('lead') || userRole.includes('manager') || userRole.includes('admin')
    );

    // ── Sidebar active-state override for team members ────────────────────
    // When a team member navigates from My Assignments → analysis-result,
    // React Router's prefix-match would incorrectly highlight "My Proposals"
    // (/proposals prefix matches /proposals/analysis-result/...).
    // We fix this by computing a forceActive flag per nav item:
    //   • My Assignments  → active on /my-proposals AND on analysis-result pages
    //   • My Proposals    → active on /proposals BUT NOT on analysis-result pages
    // Manager nav items keep React Router's default behaviour (forceActive: undefined).
    const location = useLocation();
    const isOnAnalysisResult = location.pathname.startsWith('/proposals/analysis-result');

    const navItems = [
        { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: '/dashboard' },
        ...(isManager
            ? [
                { icon: <FileText size={20} />, label: 'Proposals', path: '/proposals' },
                // { icon: <Activity size={20} />, label: 'Audit Logs', path: '/audit-logs' }
              ]
            : [
                {
                    icon: <FileText size={20} />,
                    label: 'My Assignments',
                    path: '/my-proposals',
                    // Active on /my-proposals itself OR whenever the team member
                    // is viewing an analysis-result (which they always reach from here)
                    forceActive: location.pathname === '/my-proposals' || isOnAnalysisResult
                }
            ]
        ),
        { icon: <FolderOpen size={20} />, label: 'Projects', path: '/projects' },
        ...(isManager
            ? [{ icon: <Palette size={20} />, label: 'Themes', path: '/themes' }]
            : []
        ),
        // { icon: <BarChart2 size={20} color="var(--brand-primary)" fill="var(--brand-secondary)" fillOpacity={0.15} />, label: 'Analytics', path: '/analytics' },
        // ...(isManager
        //     ? [{ icon: <Presentation size={20} color="var(--brand-primary)" fill="var(--brand-secondary)" fillOpacity={0.15} />, label: 'Presentation', path: '/presentation' }]
        //     : []
        // ),
        // PPT Editor removed from sidebar — route & files kept for future use
        ...(userDesignation.includes('admin') || userRole.includes('admin')
            ? [{ icon: <Users size={20} />, label: 'Users & Roles', path: '/users-roles' }]
            : []
        ),
    ];

    return (
        <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header-sidebar">
                <div className="logo-container">
                    <img src={edgeLogo} alt="EDGE Logo" className="edge-sidebar-logo" />
                </div>
                <button
                    className="sidebar-toggle-btn-redesign"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    <Menu size={20} />
                </button>
            </div>

            <nav className="sidebar-nav">
                {navItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => {
                            // If the item has a forceActive override (team-member items only),
                            // use that instead of React Router's default prefix-match.
                            const active = item.forceActive !== undefined ? item.forceActive : isActive;
                            return `nav-item ${active ? 'active' : ''}`;
                        }}
                        title={isCollapsed ? item.label : ''}
                    >
                        <span className="nav-icon">{item.icon}</span>
                        <span className="nav-label">{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            <div className="sidebar-footer-profile">
                <div className="sb-user-profile">
                    <UserAvatar className="sb-avatar" imageUrl={user?.avatar_url} initials={displayInitials} />
                    <div className="sb-user-info">
                        <span className="sb-user-name">{displayName}</span>
                        <span className="sb-user-role">{displayRole}</span>
                    </div>
                </div>
            </div>
        </aside>

    );
};


export default Sidebar;
